/**
 * Menu action wiring for the PA form three-dots menu: dispatches handlers,
 * modals, and analytics. Driven by the Postgres-backed `generalOrderDetails`
 * and `authOnFile` payloads.
 *
 * @module paFormThreeDotsMenu/useMenuActions
 */
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { openModal } from "risa-oasis-ui_v2";
import { usePayerNameContextFromSingleOrderLayout } from "../../../../context/payerNameContextFromSingleOrderLayout";
import { Item } from "../../../../data-model/documentModalWithSideBav";
import {
  AuthOnFile,
  GeneralOrderDetails,
} from "../../../../data-model/medicalPaOrdersModel";
import { ScreenNamesForBigQuery } from "../../../../enums/eventsData";
import { EventsName } from "../../../../enums/eventsName";
import { ModalId } from "../../../../enums/modalId";
import { Submission } from "../../../../hooks/singleMedicalPaOrderDetails/useSubmissionInformation";
import { UseHealthCareFacilityData } from "../../../../hooks/useHealthCareFacility";
import { formatJcodeIcdFromAuthOnFile } from "../../../../pages/singleOrderLayout/utils/getJCode";
import { closeModal } from "../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import { usePrimaryIcdCodes } from "../../../../utils/getPrimaryDiagnosisCodes";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../../workerManagers/workerManager";
import {
  MenuOptionId,
  MENU_OPTIONS,
} from "../../medicalPaThreeDotsMenu/constants/menuOptions";
import { useIngestInPatientGraph } from "../../medicalPaThreeDotsMenu/hooks/useIngestInPatientGraph";
import {
  handleViewDxCodes,
  handleViewGuidelines,
  handleViewInOncoEmr,
  handleViewNarGrid,
  handleViewTxPlan,
} from "../actionHandlers";

/** Inputs required to build PA form three-dots menu actions. */
export interface UseMenuActionsParams {
  items: Item[];
  healthCareFacilityData: UseHealthCareFacilityData;
  authOnFile: AuthOnFile;
  generalOrderDetails: GeneralOrderDetails;
  submissionData: Submission;
  /** BigQuery screen name used for analytics events emitted from this menu. */
  screenName: ScreenNamesForBigQuery;
  /** Re-runs the PMG exists check after ingest settles. */
  onIngestComplete?: () => void;
}

/** Return value of {@link useMenuActions}. */
export interface UseMenuActionsReturn {
  /** Runs the menu action registered for the given option id. */
  executeAction: (id: string) => void;
  /** Fires when the menu opens (hover) to log an analytics event. */
  handleMouseEnter: () => void;
  /** Shared abort handle for in-flight NAR grid fetches. */
  abortControllerRef: MutableRefObject<AbortController | null>;
  /** Redux-tracked modal id when this hook should mount companion modals. */
  openedModalId: string | null;
  /** True while patient-graph ingest is in flight. */
  isIngesting: boolean;
}

/**
 * Provides menu action handlers for the PA form three-dots menu, sourcing
 * all order context from the Postgres-backed `generalOrderDetails` and
 * `authOnFile` payloads.
 *
 * @param params - Parameters including order details, items, and analytics screen name
 * @returns Object containing action executor and related utilities
 */
export const useMenuActions = ({
  items,
  healthCareFacilityData,
  authOnFile,
  generalOrderDetails,
  submissionData,
  screenName,
  onIngestComplete,
}: UseMenuActionsParams): UseMenuActionsReturn => {
  const dispatch = useDispatch<AppDispatch>();
  const abortControllerRef = useRef<AbortController | null>(null);
  const modalOpenedByThisHookRef = useRef<string | null>(null);

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { openedModalId } = useSelector((state: RootState) => state.modal);
  const { payerName } = usePayerNameContextFromSingleOrderLayout();

  const orgId = getOrgIdForFetchExternalWorklist();
  const primaryIcdCodes = usePrimaryIcdCodes(submissionData ?? undefined);
  const jcodeIcd = useMemo(
    () => formatJcodeIcdFromAuthOnFile(authOnFile),
    [authOnFile],
  );

  const commonParams = useMemo(
    () => ({
      dispatch,
      authOnFile,
      generalOrderDetails,
      orgId: orgId ?? "",
      userId: user?.id ?? "",
      userEmail: user?.email ?? "",
      payerName,
      screenName,
    }),
    [
      dispatch,
      authOnFile,
      generalOrderDetails,
      orgId,
      user?.id,
      user?.email,
      payerName,
      screenName,
    ],
  );

  const managedModalIds = [
    ModalId.CLINICAL_DATA_TABLE,
    ModalId.DOC_VIEWER_WITH_SIDE_NAVIGATION,
    ModalId.IMAGE_VIEWER_MODAL,
    ModalId.NAR_GRID_MODAL,
  ];

  useEffect(() => {
    if (openedModalId && managedModalIds.includes(openedModalId as ModalId)) {
      openModal(openedModalId);
      modalOpenedByThisHookRef.current = openedModalId;
    }
  }, [openedModalId]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (modalOpenedByThisHookRef.current) {
        dispatch(closeModal());
        modalOpenedByThisHookRef.current = null;
      }
    };
  }, [dispatch]);

  const { ingest: ingestInPatientGraph, loading: isIngesting } =
    useIngestInPatientGraph(generalOrderDetails, orgId ?? "", onIngestComplete);

  const executeAction = useCallback(
    (id: string) => {
      switch (id as MenuOptionId) {
        case MENU_OPTIONS.VIEW_GUIDELINES:
          handleViewGuidelines({
            ...commonParams,
            items,
          });
          break;
        case MENU_OPTIONS.VIEW_TX_PLAN:
          handleViewTxPlan(commonParams);
          break;
        case MENU_OPTIONS.VIEW_IN_ONCO_EMR:
          handleViewInOncoEmr({
            ...commonParams,
            healthCareFacilityData,
          });
          break;
        case MENU_OPTIONS.VIEW_DX_CODES:
          handleViewDxCodes({
            ...commonParams,
            primaryIcdCodes: primaryIcdCodes.join(","),
          });
          break;
        case MENU_OPTIONS.VIEW_NAR_GRID:
          handleViewNarGrid({
            ...commonParams,
            abortControllerRef,
          });
          break;
        case MENU_OPTIONS.INGEST_IN_PATIENT_GRAPH:
          if (isIngesting) {
            return;
          }
          void ingestInPatientGraph();
          break;
      }
    },
    [
      commonParams,
      items,
      healthCareFacilityData,
      primaryIcdCodes,
      jcodeIcd,
      ingestInPatientGraph,
      isIngesting,
    ],
  );

  const handleMouseEnter = useCallback(() => {
    apiWorkerManager.enqueueAndProcess({
      patient_id:
        generalOrderDetails?.demographics?.patient_id ??
        generalOrderDetails?.demographics?.patient_mrn ??
        "",
      order_id: generalOrderDetails?.order_id ?? "",
      org_id: orgId ?? "",
      event_name: EventsName.ORDER_MENU_CLICKED,
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      additional_data: {
        screen_name: screenName,
        payer_name: payerName,
      },
    });
  }, [generalOrderDetails, orgId, user, payerName, screenName]);

  return {
    executeAction,
    handleMouseEnter,
    abortControllerRef,
    openedModalId,
    isIngesting,
  };
};
